import { useState } from 'react';
import { api, ApiError, type AuthStatus } from '../lib/api';
import { Wordmark } from '../components/Wordmark';

interface Props {
  onSuccess: (auth: AuthStatus) => void;
}

const DEFAULT_DEVICE_BY_PLATFORM = (() => {
  const ua = navigator.userAgent;
  if (/iPad/.test(ua)) return 'ipad';
  if (/iPhone/.test(ua)) return 'phone';
  if (/Android/.test(ua) && /Mobile/.test(ua)) return 'phone';
  if (/Android/.test(ua)) return 'tablet';
  return 'desktop';
})();

/**
 * Login screen.
 *
 * Per docs/DESIGN-DECISIONS.md §1.1: paper-and-night dark default (force,
 * not media-query based). Hero Wordmark is the single color event; inputs
 * and button are neutral-on-neutral. The Wordmark breathes via
 * simpliloquy-glow-breath-soft. Reduced-motion disables.
 *
 * Brand stance: the threshold to the family, not in the family.
 */
export function Login({ onSuccess }: Props) {
  const [secret, setSecret] = useState('');
  const [device, setDevice] = useState(DEFAULT_DEVICE_BY_PLATFORM);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const auth = await api.login(secret.trim(), device.trim());
      onSuccess(auth);
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.status === 401) setError('Secret did not match.');
        else if (err.code === 'invalid_secret_format') setError('Secret must be 16 to 128 characters.');
        else if (err.code === 'invalid_device_label')
          setError('Device label must be 1 to 32 chars (letters, numbers, space, -, _).');
        else setError(`Login failed: ${err.code}`);
      } else {
        setError('Login failed. Try again.');
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div
      className="min-h-[100dvh] w-full bg-night-900 text-paper-100 antialiased
                 flex flex-col items-center"
      style={{
        paddingTop: 'env(safe-area-inset-top)',
        paddingBottom: 'env(safe-area-inset-bottom)',
      }}
    >
      <div className="flex-1 w-full max-w-[480px] px-4 flex flex-col items-stretch">
        <div className="h-24" aria-hidden="true" />

        <div className="simpliloquy-glow-soft text-center">
          <Wordmark size="hero" />
        </div>

        <div className="h-6" aria-hidden="true" />

        <form onSubmit={submit} className="flex flex-col gap-4">
          <Field
            label="secret"
            input={
              <input
                type="password"
                inputMode="text"
                autoComplete="current-password"
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck={false}
                className="simpliloquy-input-focus
                           h-14 w-full rounded-md bg-night-800 border border-night-600
                           px-4 font-mono text-base text-paper-100 placeholder:text-night-500
                           transition-[background-color,border-color,box-shadow] duration-200"
                value={secret}
                onChange={(e) => setSecret(e.target.value)}
                placeholder="thirty-two characters"
                required
                minLength={16}
                maxLength={128}
              />
            }
          />

          <Field
            label="device"
            input={
              <input
                type="text"
                inputMode="text"
                autoComplete="off"
                className="simpliloquy-input-focus
                           h-14 w-full rounded-md bg-night-800 border border-night-600
                           px-4 font-sans text-[15px] text-paper-100 placeholder:text-night-500
                           transition-[background-color,border-color,box-shadow] duration-200"
                value={device}
                onChange={(e) => setDevice(e.target.value)}
                placeholder="ipad, phone, laptop, desktop"
                required
                maxLength={32}
              />
            }
          />

          {error && (
            <div className="text-sm text-paper-300" role="alert">
              {error}
            </div>
          )}

          <div className="h-2" aria-hidden="true" />

          <button
            type="submit"
            className="h-14 w-full rounded-md bg-night-700 hover:bg-night-600
                       border border-night-500
                       text-paper-100 font-semibold
                       focus:outline-none focus-visible:border-paper-200
                       disabled:opacity-50 disabled:cursor-not-allowed
                       transition-[background-color,border-color] duration-200"
            disabled={submitting}
          >
            {submitting ? 'Signing in.' : 'Sign in'}
          </button>
        </form>
      </div>
    </div>
  );
}

function Field({ label, input }: { label: string; input: React.ReactNode }) {
  return (
    <label className="block">
      <span
        className="block text-[10px] text-paper-400 mb-2 uppercase tracking-mono-wide font-mono"
      >
        {label}
      </span>
      {input}
    </label>
  );
}
